import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { getCookie } from "../../../lib/helpers";

// Define a service for endpoints that don't require authentication
export const partnerNoAuthApi = createApi({
  reducerPath: "partnerNoAuthApi",
  baseQuery: fetchBaseQuery({
    baseUrl: "/",
    // No prepareHeaders function - no authentication required
  }),
  endpoints: (builder) => ({
    checkInviteValidity: builder.mutation({
      query: (inviteToken: string) => ({
        url: "/api/v5/verifications/email/verify",
        method: "POST",
        body: {
          invite_token: inviteToken,
        },
      }),
    }),
    exchangePublicToken: builder.mutation({
      query: (params: {
        publicToken: string;
        blob: string | null;
        leadId: string;
      }) => ({
        url: "/api/v6/plaid/exchange_public_token",
        method: "POST",
        body: {
          public_token: params.publicToken,
          lead_id: params.leadId,
        },
      }),
    }),
    createLinkToken: builder.mutation({
      query: (params: { blob: string | null; leadId: string }) => ({
        url: "/api/v6/plaid/create_link_token",
        method: "POST",
        body: {
          lead_id: params.leadId,
        },
      }),
    }),
    fetchUserByToken: builder.query({
      query: () => {
        const userToken = getCookie("userToken");
        if (!userToken) {
          throw new Error("Missing user token");
        }
        return {
          url: "/api/v5/current_user",
          headers: {
            Authorization: `Bearer ${userToken}`,
          },
        };
      },
    }),
    fetchPlacesAutocomplete: builder.query({
      query: (searchQuery: string) => ({
        url: `/api/places/autocomplete?query=${encodeURIComponent(searchQuery)}`,
        method: "GET",
      }),
    }),
    searchPlaces: builder.query({
      query: (searchQuery: string) => ({
        url: "/places/search",
        method: "POST",
        body: { q: searchQuery },
      }),
    }),
    fetchPlaceDetails: builder.mutation({
      query: (placeId: string) => ({
        url: `/places/${encodeURIComponent(placeId)}/details`,
        method: "POST",
      }),
    }),
    createLead: builder.mutation({
      query: (leadData: {
        first_name: string;
        last_name: string;
        email: string;
        phone: string;
        company_name: string;
        job_title?: string;
      }) => ({
        url: "/api/v5/leads",
        method: "POST",
        body: {
          lead: leadData,
        },
      }),
    }),
    saveLeadData: builder.mutation({
      query: (params: { leadId: string; placeDetails: any[] }) => ({
        url: "/places/save_lead_data",
        method: "POST",
        body: {
          lead_id: params.leadId,
          brand_data: params.placeDetails,
        },
      }),
    }),
  }),
});

export const {
  useCheckInviteValidityMutation,
  useFetchUserByTokenQuery,
  useExchangePublicTokenMutation,
  useLazyFetchUserByTokenQuery,
  useCreateLinkTokenMutation,
  useFetchPlacesAutocompleteQuery,
  useLazyFetchPlacesAutocompleteQuery,
  useSearchPlacesQuery,
  useLazySearchPlacesQuery,
  useFetchPlaceDetailsMutation,
  useSaveLeadDataMutation,
  useCreateLeadMutation,
} = partnerNoAuthApi;
